import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Crown, User, Settings, Mail, Heart } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import NotificationBell from '@/components/NotificationBell';
import LocationSelector from '@/components/LocationSelector';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import QuickSearch from '@/components/QuickSearch';

// Navigation menu configuration
const businessToolsLinks = [
  { to: '/businesses', label: '📋 Business Directory' },
  { to: '/business-map', label: '🗺️ Interactive Map' },
  { to: '/service-providers', label: '🤝 Service Provider Marketplace', highlight: true },
  { to: '/capital-marketplace', label: '💰 Capital & Lending', highlight: true },
  { to: '/business-formation', label: '📝 Formation Wizard' },
  { to: '/permit-lookup', label: '🔍 Permit Lookup' },
  { to: '/professional-services', label: '⭐ Professional Services' },
  { to: '/ai-assistant', label: '💬 AI Assistant', style: 'text-purple-600 hover:bg-purple-50 font-semibold' },
  { to: '/test-your-knowledge', label: '🧠 Test Your Knowledge', style: 'text-blue-600 hover:bg-blue-50 font-semibold' },
];

// "Learn" — purely educational. Industry suites moved here as a flyout.
const learningLinks = [
  { to: '/ai-fundamentals', label: '🧠 AI Fundamentals', style: 'text-blue-600 hover:bg-blue-50 font-semibold' },
  { to: '/ai-curriculum', label: '📖 AI Training', style: 'text-purple-600 hover:bg-purple-50' },
  { to: '/lms', label: '📚 My Learning', style: 'text-indigo-600 hover:bg-indigo-50' },
  { to: '/resources', label: '📖 Learning Library' },
];

const learnMenuLinks = [
  { section: null, items: learningLinks },
  { section: 'Specialized Tracks', items: [
    { to: '/industry-suites', label: 'Industry Suites', submenuId: 'industrySuites' },
  ]},
  { section: null, items: [
    { to: '/workforce', label: '💼 wUrkForce Development', style: 'bg-blue-600 text-white hover:bg-blue-700 font-semibold text-center' },
    { to: '/badges', label: '🏅 Open Badges', style: 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600 text-center' },
  ]},
];

// "Grow" — NEW top-level. Surfaces concierges/capital/programs that were
// previously buried 3 clicks deep under Learn → Business Growth.
const growMenuLinks = [
  { section: 'Start Here', items: [
    { to: '/launch-roadmap', label: '🚀 10-Step Launch Roadmap', style: 'bg-gradient-to-r from-emerald-600 to-emerald-800 text-white hover:from-emerald-700 hover:to-emerald-900 font-bold' },
    { to: '/launch-calculator', label: '🧮 Startup Cost Calculator', style: 'bg-amber-600 text-white hover:bg-amber-700 font-semibold' },
    { to: '/business-structure', label: '⚖️ Business Structure Chooser', style: 'bg-indigo-700 text-white hover:bg-indigo-800 font-semibold' },
    { to: '/business-plan', label: '📝 Business Plan Builder (AI)', style: 'bg-purple-700 text-white hover:bg-purple-800 font-semibold' },
    { to: '/lender-match', label: '🏦 Louisiana Lender Match', style: 'bg-teal-700 text-white hover:bg-teal-800 font-semibold' },
    { to: '/certifications', label: '🛡️ Certification Tracker', style: 'bg-slate-700 text-white hover:bg-slate-800 font-semibold' },
  ]},
  { section: 'Concierges', items: [
    { to: '/grant-concierge', label: '🤖 Grant Concierge AI', style: 'bg-[#006847] text-white hover:bg-[#004d35] font-semibold' },
    { to: '/grant-scout', label: '🎯 Grant Scout (Grants.gov)', style: 'bg-emerald-700 text-white hover:bg-emerald-800 font-semibold' },
    { to: '/nonprofit-grants', label: '🏢 Nonprofit Grants Pipeline', style: 'bg-teal-700 text-white hover:bg-teal-800 font-semibold' },
    { to: '/compliance-concierge', label: '🛡️ Stay Right (Compliance)', style: 'bg-amber-600 text-white hover:bg-amber-700 font-semibold' },
    { to: '/federal-contracting', label: '🏛️ Federal Contracting', style: 'bg-blue-700 text-white hover:bg-blue-800 font-semibold' },
  ]},
  { section: 'Capital & Funding', items: [
    { to: '/grants/tracker', label: '📅 Grant Deadline Tracker', style: 'bg-emerald-700 text-white hover:bg-emerald-800 font-semibold' },
    { to: '/capital-readiness', label: '💰 Capital Readiness Hub', style: 'bg-[#388E3C] text-white hover:bg-[#1E4D2E]' },
    { to: '/defi-lending', label: '🪙 DeFi Lending', style: 'bg-emerald-700 text-white hover:bg-emerald-800' },
    { to: '/financial-literacy', label: '📊 Financial Literacy', style: 'bg-emerald-600 text-white hover:bg-emerald-700' },
  ]},
  { section: 'Operations', items: [
    { to: '/pitch-practice', label: '🎤 AI Pitch Practice', style: 'bg-rose-600 text-white hover:bg-rose-700 font-semibold' },
    { to: '/sop-builder', label: '📋 SOP Builder', style: 'bg-slate-700 text-white hover:bg-slate-800 font-semibold' },
    { to: '/nonprofit-ops', label: '🏢 Nonprofit Ops Suite', style: 'bg-teal-700 text-white hover:bg-teal-800 font-semibold' },
    { to: '/retail-launchpad', label: '🛍️ Retail Launchpad', style: 'bg-purple-600 text-white hover:bg-purple-700' },
  ]},
  { section: 'Programs & Resources', items: [
    { to: '/nonprofits', label: '🏛️ For Nonprofits · PROOF', style: 'bg-gradient-to-r from-emerald-700 to-emerald-900 text-white hover:from-emerald-800 hover:to-emerald-950 font-bold' },
    { to: '/nonprofits/start-a-nonprofit', label: '📘 How to Start a Nonprofit (LA)', style: 'bg-sky-50 text-sky-800 hover:bg-sky-100 font-semibold' },
    { to: '/nonprofits/advocacy', label: '📣 Federal Funding Advocacy Guide', style: 'bg-amber-50 text-amber-900 hover:bg-amber-100 font-semibold' },
    { to: '/nonprofits/signup', label: '✨ Apply for PROOF Free', style: 'bg-emerald-600 text-white hover:bg-emerald-700 font-bold' },
    { to: '/nonprofits/directory', label: '📇 PROOF Directory', style: 'bg-emerald-100 text-emerald-900 hover:bg-emerald-200 font-semibold' },
    { to: '/nonprofits/proof-free-tier', label: '🎁 PROOF Free Tier', style: 'bg-amber-100 text-amber-900 hover:bg-amber-200 font-semibold' },
    { to: '/nonprofits/embed-kit', label: '📎 PROOF Verified Embed Kit', style: 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 font-semibold' },
    { to: '/needs-assessment', label: '🤝 Community Needs Assessment', style: 'bg-rose-50 text-rose-800 hover:bg-rose-100 font-semibold' },
    { to: '/dowurk/impact', label: '📊 PROOF Network Impact', style: 'bg-indigo-50 text-indigo-800 hover:bg-indigo-100 font-semibold' },
    { to: '/premium/ai-dashboard', label: '🔒 Premium AI Dashboard', style: 'bg-[#1E4D2E] text-[#FFC107] hover:bg-[#388E3C]' },
    { to: '/ai-generalist', label: '🎓 AI Generalist Program', style: 'bg-gradient-to-r from-[#388E3C] to-[#8BC34A] text-white hover:from-[#1E4D2E] hover:to-[#388E3C]' },
    { to: '/institutional-licensing', label: '🏛️ Institutional Licensing', style: 'bg-indigo-600 text-white hover:bg-indigo-700' },
    { to: '/mentors', label: '🤝 Mentor Match' },
    { to: '/global-biz-resources', label: '🌍 Global Resources' },
    { to: '/louisiana-biz-resources', label: '⚜️ Louisiana Resources' },
  ]},
];

const industrySuitesSubmenu = {
  label: 'Industry Suites',
  items: [
    { to: '/photography-suite', label: '📸 Photography' },
    { to: '/real-estate-suite', label: '🏠 Real Estate' },
    { to: '/restaurant-suite', label: '🍽️ Restaurant' },
    { to: '/consultant-suite', label: '💼 Consultant' },
  ],
};

const sevenFsLinks = [
  { to: '/framework', label: '🎯 The Seven F\'s FramewUrk', style: 'text-[#A4D65E] font-bold', isOverview: true },
  { to: '/faith', label: '🧭 Faith wUrks', style: 'text-amber-500 hover:bg-amber-50' },
  { to: '/fitness', label: '💪 wUrkFit', style: 'text-rose-500 hover:bg-rose-50' },
  { to: '/foundation', label: '📚 School 4 Thought', style: 'text-purple-500 hover:bg-purple-50' },
  { to: '/food', label: '🍽️ Farm2Table', style: 'text-orange-500 hover:bg-orange-50' },
  { to: '/legacystudio', label: '🎬 LegacyStudio', style: 'text-violet-500 hover:bg-violet-50' },
  { to: '/financial-literacy', label: '💰 Financial Literacy', style: 'text-emerald-500 hover:bg-emerald-50' },
];

// Mental Wealth flyout — exposes the Audit → Pathway → Wall journey.
const mentalWealthSubmenu = {
  label: 'Mental Wealth',
  items: [
    { to: '/mental-health', label: '🧠 Mental Health Center', style: 'bg-gradient-to-r from-emerald-600 to-amber-500 text-white hover:from-emerald-700 hover:to-amber-600 font-semibold' },
    { to: '/mental-health/audit', label: '📋 Burnout Audit' },
    { to: '/proof/resilience-pathway', label: '🛡️ 30-Day Resilience Pathway', style: 'text-emerald-700 font-semibold' },
    { to: '/proof/resilience-wall', label: '🧱 The Resilience Wall' },
    { to: '/mental-health/resources', label: '📚 Crisis & Wellness Resources' },
  ],
};

const communityLinks = [
  { section: null, items: [
    { to: '/next-step', label: '🧭 What\'s My Next Step', style: 'bg-gradient-to-r from-slate-900 to-slate-700 text-white hover:from-slate-800 hover:to-slate-600 font-semibold' },
    { to: '/dowurk-score', label: '⭐ DowUrk Score', style: 'bg-gradient-to-r from-emerald-700 to-emerald-900 text-white hover:from-emerald-800 hover:to-emerald-950 font-semibold' },
    { to: '/mental-health', label: 'Mental Wealth', submenuId: 'mentalWealth' },
    { to: '/framework', label: '🎯 Seven F\'s FramewUrk', submenuId: 'sevenFs' },
  ]},
  { section: 'Service & Support', items: [
    { to: '/wins', label: '🏆 The Win Wall', style: 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:from-amber-600 hover:to-orange-700 font-semibold' },
    { to: '/skills-barter', label: '🤝 Skills Barter', style: 'bg-gradient-to-r from-purple-600 to-indigo-700 text-white hover:from-purple-700 hover:to-indigo-800 font-semibold' },
    { to: '/volunteer-hub', label: '💝 Volunteer Hub', style: 'bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600' },
    { to: '/veterans', label: '🎖️ Veterans Support', style: 'bg-[#006847] text-white hover:bg-[#005a3c]' },
    { to: '/disaster-hub', label: '🌪️ Disaster Hub', style: 'bg-amber-500 text-white hover:bg-amber-600' },
  ]},
  { section: 'Connect', items: [
    { to: '/forum', label: '💬 Community Forum', style: 'text-[#388E3C] font-semibold' },
    { to: '/community', label: '🌐 Community Wall' },
    { to: '/events', label: '📅 Events' },
    { to: '/gratitude-wall', label: '🙏 Gratitude Wall' },
    { to: '/partnerships', label: '🤝 Partnerships' },
  ]},
];

import { ChevronLeft } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

function NavDropdown({ trigger, links, width = 'w-52', submenus = null, testid }) {
  // submenus: { [submenuId]: { label, items, theme?: 'dark'|'light' } }
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [triggerRect, setTriggerRect] = useState(null);
  const hideTimeoutRef = useRef(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnterTrigger = (e, submenuId) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setTriggerRect(rect);
    setActiveSubmenu(submenuId);
  };

  const handleMouseLeaveTrigger = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setActiveSubmenu(null);
    }, 150);
  };

  const handleMouseEnterSubmenu = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleMouseLeaveSubmenu = () => {
    setActiveSubmenu(null);
  };

  const handleTriggerClick = (e, submenuId) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setTriggerRect(rect);
    setActiveSubmenu(activeSubmenu === submenuId ? null : submenuId);
  };

  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-gray-700 hover:text-[#388E3C] text-sm" data-testid={testid}>
            {trigger}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className={`${width} p-2`}>
              {links.map((item, idx) => {
                if (item.section !== undefined) {
                  return (
                    <div key={item.section || `section-${idx}`}>
                      {item.section && (
                        <>
                          {idx > 0 && <div className="border-t border-gray-200 my-2 pt-2"></div>}
                          <p className="px-3 py-1 text-xs font-bold text-gray-400 uppercase">{item.section}</p>
                        </>
                      )}
                      {item.items.map(link => {
                        const linkHasSubmenu = link.submenuId && submenus?.[link.submenuId];
                        return linkHasSubmenu ? (
                          <div
                            key={link.to || link.submenuId}
                            className="relative"
                            data-testid={`submenu-trigger-${link.submenuId}`}
                          >
                            <div
                              className="flex items-center justify-between px-3 py-2.5 text-sm rounded cursor-pointer bg-gradient-to-r from-[#1E4D2E]/10 to-[#388E3C]/10 hover:from-[#1E4D2E]/20 hover:to-[#388E3C]/20 border border-[#388E3C]/30 mb-1"
                              onClick={(e) => handleTriggerClick(e, link.submenuId)}
                              onMouseEnter={(e) => handleMouseEnterTrigger(e, link.submenuId)}
                              onMouseLeave={handleMouseLeaveTrigger}
                            >
                              <ChevronLeft className={`h-4 w-4 text-[#388E3C] transition-transform ${activeSubmenu === link.submenuId ? 'rotate-90' : ''}`} />
                              <span className="flex-1 text-center font-semibold text-[#388E3C]">{link.label}</span>
                            </div>
                          </div>
                        ) : (
                          <Link
                            key={link.to}
                            to={link.to}
                            className={`block px-3 py-2 text-sm rounded mb-1 ${link.style || 'text-gray-700 hover:bg-gray-100'} ${link.highlight ? 'font-semibold' : ''}`}
                          >
                            {link.label}
                          </Link>
                        );
                      })}
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`block px-3 py-2 text-sm rounded ${item.style || 'text-gray-700 hover:bg-gray-100'} ${item.highlight ? 'text-[#388E3C] font-semibold' : ''}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>

      {/* Portal-based flyout — unified for all submenus, with optional dark theme. */}
      {activeSubmenu && submenus?.[activeSubmenu] && triggerRect && (() => {
        const sub = submenus[activeSubmenu];
        const isDark = sub.theme === 'dark';
        const flyoutTestId = activeSubmenu === 'sevenFs' ? 'seven-fs-flyout' : `submenu-flyout-${activeSubmenu}`;
        return createPortal(
          <div
            className={`fixed rounded-lg shadow-2xl p-3 z-[99999] ${isDark ? 'bg-gradient-to-b from-[#1E4D2E] to-[#006847]' : 'bg-white border border-gray-200'}`}
            data-testid={flyoutTestId}
            style={{
              top: triggerRect.top,
              left: triggerRect.left - (isDark ? 240 : 260),
              width: isDark ? '230px' : '250px',
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
            onMouseEnter={handleMouseEnterSubmenu}
            onMouseLeave={handleMouseLeaveSubmenu}
          >
            <p className={`px-3 py-2 text-xs font-bold uppercase border-b mb-2 ${isDark ? 'text-[#A4D65E] border-white/20' : 'text-gray-400 border-gray-200'}`}>
              {sub.label}
            </p>
            {sub.items.map((subLink) => (
              <Link
                key={subLink.to}
                to={subLink.to}
                className={
                  isDark
                    ? `block px-3 py-2.5 text-sm rounded-md transition-colors font-medium mb-1 ${subLink.isOverview ? 'bg-[#A4D65E]/20 text-[#A4D65E] hover:bg-[#A4D65E]/30 border border-[#A4D65E]/30' : 'text-white hover:bg-white/20'}`
                    : `block px-3 py-2 text-sm rounded mb-1 ${subLink.style || 'text-gray-700 hover:bg-gray-100'}`
                }
                onClick={() => setActiveSubmenu(null)}
              >
                {subLink.label}
              </Link>
            ))}
          </div>,
          document.body
        );
      })()}
    </NavigationMenu>
  );
}

export function DesktopNav({ user, onLogout }) {
  const { t } = useTranslation();

  // ==================== 5-ITEM COMPRESSED NAV (Feb 2026 IA re-org) ====================
  // Compressed from 7 to 5 by merging Find Capital + For Entrepreneurs -> Start Your Business,
  // and For Nonprofits + Prove Impact -> Run Your Nonprofit. Community & Support and Give
  // preserved. About kept as-is. No links removed — everything routes through mega-menus.

  const startYourBusinessLinks = [
    { section: 'Start Here', items: [
      { to: '/launch-roadmap', label: '🚀 10-Step Launch Roadmap', style: 'bg-gradient-to-r from-emerald-600 to-emerald-800 text-white hover:from-emerald-700 hover:to-emerald-900 font-bold' },
      { to: '/launch-calculator', label: '🧮 Startup Cost Calculator', style: 'bg-amber-600 text-white hover:bg-amber-700 font-semibold' },
      { to: '/business-structure', label: '⚖️ Business Structure Chooser', style: 'bg-indigo-700 text-white hover:bg-indigo-800 font-semibold' },
      { to: '/business-plan', label: '📝 AI Business Plan Builder', style: 'bg-purple-700 text-white hover:bg-purple-800 font-semibold' },
      { to: '/business-formation', label: '📝 Formation Wizard' },
    ]},
    { section: 'Find Capital', items: [
      { to: '/grant-concierge', label: '🤖 Grant Concierge AI', style: 'bg-[#006847] text-white hover:bg-[#004d35] font-semibold' },
      { to: '/grant-scout', label: '🎯 Grant Scout (Grants.gov)', style: 'bg-emerald-700 text-white hover:bg-emerald-800 font-semibold' },
      { to: '/compliance-concierge', label: '🛡️ Stay Right (Compliance)', style: 'bg-amber-600 text-white hover:bg-amber-700 font-semibold' },
      { to: '/federal-contracting', label: '🏛️ Federal Contracting', style: 'bg-blue-700 text-white hover:bg-blue-800 font-semibold' },
      { to: '/capital-marketplace', label: '💰 Capital & Lending Marketplace' },
      { to: '/lender-match', label: '🏦 Louisiana Lender Match' },
      { to: '/capital-readiness', label: '📊 Capital Readiness Hub' },
      { to: '/defi-lending', label: '🪙 DeFi Lending' },
      { to: '/financial-literacy', label: '📊 Financial Literacy' },
    ]},
    { section: 'Discover', items: [
      { to: '/businesses', label: '📋 Business Directory' },
      { to: '/business-map', label: '🗺️ Interactive Business Map' },
      { to: '/service-providers', label: '🤝 Service Provider Marketplace' },
      { to: '/professional-services', label: '⭐ Professional Services' },
      { to: '/permit-lookup', label: '🔍 Permit Lookup' },
    ]},
    { section: 'Operations & Growth', items: [
      { to: '/pitch-practice', label: '🎤 AI Pitch Practice', style: 'bg-rose-600 text-white hover:bg-rose-700 font-semibold' },
      { to: '/sop-builder', label: '📋 SOP Builder' },
      { to: '/retail-launchpad', label: '🛍️ Retail Launchpad' },
      { to: '/workforce', label: '💼 wUrkForce Development', style: 'bg-gradient-to-r from-blue-700 to-emerald-700 text-white hover:from-blue-800 hover:to-emerald-800 font-bold' },
      { to: '/certifications', label: '🛡️ Certification Tracker' },
    ]},
    { section: 'Resources', items: [
      { to: '/resources', label: '📖 Resource Library' },
      { to: '/blog', label: '✍️ Blog & Podcast' },
      { to: '/global-biz-resources', label: '🌍 Global Biz Resources' },
      { to: '/louisiana-biz-resources', label: '⚜️ Louisiana Biz Resources' },
    ]},
  ];

  const runYourNonprofitLinks = [
    { section: 'PROOF for Cohorts', items: [
      { to: '/nonprofits', label: '🏛️ PROOF for Cohorts', style: 'bg-gradient-to-r from-emerald-700 to-emerald-900 text-white hover:from-emerald-800 hover:to-emerald-950 font-bold' },
      { to: '/pricing', label: '💎 PROOF Pricing & Plans', style: 'bg-amber-500 text-white hover:bg-amber-600 font-bold' },
      { to: '/nonprofits/signup', label: '✨ Apply for PROOF Free', style: 'bg-emerald-600 text-white hover:bg-emerald-700 font-bold' },
      { to: '/nonprofits/start-a-nonprofit', label: '📘 How to Start a Nonprofit (LA)' },
      { to: '/nonprofits/advocacy', label: '📣 Federal Funding Advocacy' },
    ]},
    { section: 'Grants & Compliance', items: [
      { to: '/nonprofit-grants', label: '🏢 Nonprofit Grants Pipeline', style: 'bg-teal-700 text-white hover:bg-teal-800 font-semibold' },
      { to: '/grants/tracker', label: '📅 Grant Deadline Tracker' },
      { to: '/grant-concierge', label: '🤖 Grant Concierge AI' },
      { to: '/compliance-concierge', label: '🛡️ Stay Right (Compliance)' },
      { to: '/nonprofit-ops', label: '🏢 Nonprofit Ops Suite' },
    ]},
    { section: 'Prove Impact', items: [
      { to: '/dowurk/impact', label: '📊 PROOF Network Impact', style: 'bg-indigo-700 text-white hover:bg-indigo-800 font-bold' },
      { to: '/proof', label: '🎯 PROOF Overview', style: 'bg-emerald-700 text-white hover:bg-emerald-800 font-bold' },
      { to: '/badges', label: '🏅 Open Badges' },
      { to: '/proof/pulse', label: '📈 PROOF Scoreboard' },
      { to: '/simulators', label: '🕶️ Immersive Simulators' },
    ]},
    { section: 'Discover', items: [
      { to: '/nonprofits/directory', label: '📇 PROOF Directory' },
      { to: '/nonprofits/proof-free-tier', label: '🎁 Free Tier Details' },
      { to: '/nonprofits/embed-kit', label: '📎 PROOF Embed Kit' },
      { to: '/needs-assessment', label: '🤝 Community Needs Assessment' },
    ]},
    { section: 'Resilience', items: [
      { to: '/proof/resilience-pathway', label: '🛡️ 30-Day Resilience Pathway', style: 'text-emerald-700 font-semibold' },
      { to: '/proof/resilience-wall', label: '🧱 Resilience Wall' },
    ]},
  ];

  const communityNavLinks = [
    { section: 'Tracks', items: [
      { to: '/youth-track', label: '🎓 Youth Track (13–18)', style: 'bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:from-purple-700 hover:to-pink-600 font-bold' },
      { to: '/community-track', label: '💼 Community Track', style: 'bg-gradient-to-r from-emerald-600 to-teal-700 text-white hover:from-emerald-700 hover:to-teal-800 font-bold' },
      { to: '/framework', label: "🎯 Seven F's Programs", submenuId: 'sevenFs' },
    ]},
    { section: 'Wellbeing & Support', items: [
      { to: '/mental-health', label: '🧠 Mental Health Center', style: 'bg-gradient-to-r from-emerald-600 to-amber-500 text-white hover:from-emerald-700 hover:to-amber-600 font-semibold' },
      { to: '/disaster-hub', label: '🌪️ Disaster Hub', style: 'bg-amber-500 text-white hover:bg-amber-600 font-semibold' },
      { to: '/veterans', label: '🎖️ Veterans Support', style: 'bg-[#006847] text-white hover:bg-[#005a3c] font-semibold' },
    ]},
    { section: 'Connect', items: [
      { to: '/forum', label: '💬 Community Discussion', style: 'text-[#388E3C] font-semibold' },
      { to: '/community', label: '🌐 Community Wall' },
      { to: '/wins', label: '🏆 The Win Wall' },
      { to: '/mentors', label: '🤝 Mentor Match' },
      { to: '/events', label: '📅 Events' },
      { to: '/volunteer-hub', label: '💝 Volunteer Hub' },
      { to: '/skills-barter', label: '🤝 Skills Barter' },
      { to: '/gratitude-wall', label: '🙏 Gratitude Wall' },
    ]},
  ];

  const communitySubmenus = {
    sevenFs: {
      label: "Seven F's FramewUrk",
      theme: 'dark',
      items: sevenFsLinks,
    },
  };

  return (
    <div data-testid="desktop-navigation" className="hidden min-[1800px]:flex items-center space-x-1 flex-1 [&_.relative.z-10.flex]:!flex-none">
      {/* 1. ABOUT — moved to first position May 2026. Stripped of duplicates;
          only contains pages that are NOT in the other top-level dropdowns. */}
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger className="text-gray-700 hover:text-[#388E3C] transition px-2 py-2 text-sm bg-transparent">
              About
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="w-64 p-2" data-testid="about-dropdown">
                <Link to="/about" className="block px-3 py-2 text-sm rounded-md hover:bg-gray-100" data-testid="about-link-about-us">About Us</Link>
                <Link to="/our-pledge" className="block px-3 py-2 text-sm rounded-md hover:bg-gray-100" data-testid="about-link-pledge">Our Pledge</Link>
                <Link to="/why-dowurk" className="block px-3 py-2 text-sm rounded-md hover:bg-gray-100 text-[#006847] font-medium" data-testid="about-link-why">Why DowUrk?</Link>
                <Link to="/partnerships" className="block px-3 py-2 text-sm rounded-md hover:bg-gray-100" data-testid="about-link-partnerships">Partnerships</Link>
                <Link to="/press-release" className="block px-3 py-2 text-sm rounded-md hover:bg-gray-100" data-testid="about-link-press">Press Release</Link>
                <Link to="/contact" className="block px-3 py-2 text-sm rounded-md hover:bg-gray-100" data-testid="about-link-contact">Contact Us</Link>

                <div className="border-t my-2"></div>
                <Link
                  to="/tools"
                  className="block px-3 py-2 text-sm rounded-md bg-gradient-to-r from-emerald-700 to-emerald-900 text-white hover:from-emerald-800 hover:to-emerald-950 font-bold text-center"
                  data-testid="about-link-all-tools"
                >
                  🛠️ All Tools Directory
                </Link>

                <div className="border-t my-2"></div>
                <p className="px-3 py-1 text-[10px] font-bold uppercase text-slate-400">Membership</p>
                <Link to={user ? "/premium" : "/membership"} className="block px-3 py-1.5 text-xs rounded-md hover:bg-yellow-50 text-yellow-700 font-medium" data-testid="about-link-premium">👑 Premium Membership</Link>
                <Link to="/enterprise" className="block px-3 py-1.5 text-xs rounded-md hover:bg-slate-100 text-slate-700" data-testid="about-link-enterprise">🏛️ Enterprise Portal</Link>

                <div className="border-t my-2"></div>
                <p className="px-3 py-1 text-[10px] font-bold uppercase text-slate-400">Support</p>
                <Link to="/fundraiser" className="block px-3 py-1.5 text-xs rounded-md hover:bg-emerald-50 text-emerald-700" data-testid="about-link-dream">💚 Dream Project</Link>
                <Link to="/shop" className="block px-3 py-1.5 text-xs rounded-md hover:bg-gray-100 text-slate-600" data-testid="about-link-shop">🛒 Shop Merch</Link>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      {/* 2. START YOUR BUSINESS (merges Find Capital + For Entrepreneurs) */}
      <NavDropdown trigger="🚀 Start Your Business" links={startYourBusinessLinks} width="w-72" testid="nav-trigger-start-business" />

      {/* 3. RUN YOUR NONPROFIT (merges For Nonprofits + Prove Impact) */}
      <NavDropdown trigger="🏛️ Run Your Nonprofit" links={runYourNonprofitLinks} width="w-72" testid="nav-trigger-run-nonprofit" />

      {/* 4. COMMUNITY & SUPPORT */}
      <NavDropdown trigger="👥 Community & Support" links={communityNavLinks} width="w-72" submenus={communitySubmenus} testid="nav-trigger-community" />

      {/* 5. GIVE */}
      <Link
        to="/donate"
        className="text-rose-600 hover:text-rose-700 transition px-3 py-2 text-sm font-bold inline-flex items-center gap-1"
        data-testid="nav-give-primary"
      >
        <Heart className="h-4 w-4" /> Give
      </Link>

      {user ? (
        <div className="flex items-center space-x-2 ml-auto">
          <QuickSearch />
          <LanguageSwitcher variant="minimal" />
          <LocationSelector variant="badge" />
          <NotificationBell />
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="flex items-center space-x-1 px-2">
              {(user.profile_picture || user.profile_image) ? (
                <img
                  src={user.profile_picture || user.profile_image}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="h-6 w-6 rounded-full object-cover"
                />
              ) : (
                <User className="h-4 w-4" />
              )}
              <span className="max-w-[80px] truncate">{user.full_name}</span>
            </Button>
          </Link>
          {(user.user_type === 'admin' || user.email === 'info@dowurktoday.org' || user.email === 'info@dowurktoday.com') && (
            <Link to="/admin">
              <Button variant="outline" size="sm" className="border-purple-600 text-purple-600 hover:bg-purple-50 px-2">
                <Settings className="h-4 w-4 mr-1" />
                Admin
              </Button>
            </Link>
          )}
          <Link to="/account/settings">
            <Button variant="outline" size="sm" className="border-gray-400 text-gray-600 hover:bg-gray-50 px-2">
              <Settings className="h-4 w-4" />
            </Button>
          </Link>
          <Button 
            onClick={onLogout} 
            variant="outline" 
            size="sm"
            className="border-[#006847] text-[#006847] hover:bg-[#006847] hover:text-white px-2"
          >
            {t('nav.logout')}
          </Button>
        </div>
      ) : (
        <div className="flex items-center space-x-2 ml-auto">
          <QuickSearch />
          <LanguageSwitcher variant="minimal" />
          <LocationSelector variant="compact" />
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-gray-300 text-gray-700 hover:bg-gray-50 px-2 flex items-center gap-1.5"
            onClick={() => {
              const redirectUrl = window.location.origin + '/auth/callback';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            title="Sign in with Google"
            data-testid="nav-google-signin-chip"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.28 1.48-1.12 2.73-2.38 3.57v2.96h3.85c2.25-2.08 3.58-5.14 3.58-8.77z"/>
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.85-2.96c-1.07.72-2.44 1.15-4.08 1.15-3.14 0-5.8-2.12-6.75-4.97H1.3v3.06C3.27 21.3 7.31 24 12 24z"/>
              <path fill="#FBBC05" d="M5.25 14.31c-.24-.72-.38-1.49-.38-2.31s.14-1.59.38-2.31V6.63H1.3C.47 8.28 0 10.09 0 12s.47 3.72 1.3 5.37l3.95-3.06z"/>
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.27 2.7 1.3 6.63l3.95 3.06C6.2 6.87 8.86 4.75 12 4.75z"/>
            </svg>
            <span className="hidden xl:inline">Google</span>
          </Button>
          <Link to="/login">
            <Button variant="outline" size="sm" className="border-[#006847] text-[#006847] px-2">
              {t('nav.login')}
            </Button>
          </Link>
          <Link to="/register">
            <Button size="sm" className="bg-gradient-to-r from-[#A4D65E] to-[#006847] hover:opacity-90 px-2">
              {t('nav.register')}
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

export default DesktopNav;
